import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, Loader2, Home, KeyRound } from "lucide-react";
import { api, setAdminToken } from "@/lib/api";
import { PrimaryLogo } from "@/components/Brand";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  // Two-step: after password, a 6-digit code is emailed.
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");

  // Redirect to setup if no real admin exists.
  useEffect(() => {
    api.get("/admin/setup-status").then(({ data }) => {
      if (data.needs_setup) navigate("/admin/setup", { replace: true });
    }).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (params.get("setup") === "success") {
      toast.success("Setup complete. The demo account has been removed. Please sign in with your new credentials.");
    } else if (params.get("setup") === "done") {
      toast.info("Setup is already complete.");
    } else if (params.get("expired") === "1") {
      toast.info("Signed out for inactivity. Please sign in again.");
    }
  }, [params]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { email: email.trim(), password });
      if (data.code_required) {
        setChallenge(data.challenge);
        toast.success(data.message || "Check your email for a 6-digit sign-in code.");
      } else {
        setAdminToken(data.token);
        toast.success("Welcome, " + (data.name || data.email));
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not sign in.");
    } finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/verify-code", { challenge, code: code.trim() });
      setAdminToken(data.token);
      toast.success("Welcome, " + (data.name || data.email));
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "That code did not work.");
    } finally { setBusy(false); }
  };

  const resendCode = async () => {
    try {
      const { data } = await api.post("/admin/resend-code", { challenge });
      toast.success(data?.message || "A fresh code is on its way.");
    } catch (err) {
      toast.error("Could not resend the code. Please sign in again.");
    }
  };

  const submitForgot = async (e) => {
    e?.preventDefault();
    setForgotBusy(true);
    try {
      const { data } = await api.post("/admin/forgot-password", { email: forgotEmail.trim() });
      toast.success(data?.message || "If that email is registered, a reset link has been sent. Please check your inbox.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "We couldn't send the reset link. Please try again shortly.");
    } finally {
      setForgotBusy(false);
      setForgotOpen(false);
      setForgotEmail("");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: "#F4F8FB" }}>
      <div className="max-w-md w-full">
        <Link to="/" className="inline-flex items-center gap-2 mb-6 text-sm" style={{ color: "#0B2D59" }}>
          <Home className="w-4 h-4" /> Back to home
        </Link>
        <div className="flex justify-center mb-6">
          <PrimaryLogo maxWidth={240} />
        </div>
        <div className="p2p-card p-8">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-6 h-6" style={{ color: "#0B2D59" }} />
            <h1 className="font-serif text-2xl" style={{ color: "#0B2D59" }}>Administrator sign in</h1>
          </div>
          <p className="text-sm" style={{ color: "#475569" }}>
            {challenge
              ? "We emailed you a 6-digit code. Enter it below to finish signing in."
              : "Enter your email and password to access the admin dashboard."}
          </p>

          {challenge ? (
            <form onSubmit={verifyCode} className="space-y-4 mt-6" data-testid="admin-code-form">
              <div>
                <label htmlFor="login-code" className="p2p-label">6-digit code</label>
                <input id="login-code" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  className="p2p-input text-center tracking-[0.5em] text-xl"
                  data-testid="admin-code-input"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required autoComplete="one-time-code" autoFocus />
              </div>
              <button type="submit" className="p2p-btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="admin-code-submit" disabled={busy || code.length !== 6}>
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? "Verifying…" : "Verify code"}
              </button>
              <div className="flex justify-between text-xs">
                <button type="button" className="underline" style={{ color: "#0B2D59" }}
                  data-testid="admin-code-resend" onClick={resendCode}>
                  Resend code
                </button>
                <button type="button" className="underline" style={{ color: "#475569" }}
                  onClick={() => { setChallenge(""); setCode(""); }}>
                  Start over
                </button>
              </div>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <label htmlFor="login-email" className="p2p-label">Email</label>
              <input id="login-email" type="email" className="p2p-input" data-testid={ADMIN.loginEmail}
                value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="login-pw" className="p2p-label">Password</label>
                <button type="button" onClick={() => setForgotOpen(true)}
                  data-testid="admin-forgot-link"
                  className="text-xs underline mb-2" style={{ color: "#0B2D59" }}>
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input id="login-pw" type={show ? "text" : "password"} className="p2p-input pr-12"
                  data-testid={ADMIN.loginPassword}
                  value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                <button type="button" onClick={() => setShow((s) => !s)}
                  data-testid="admin-toggle-show"
                  aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full"
                  style={{ color: "#475569" }}>
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" className="p2p-btn-primary w-full inline-flex items-center justify-center gap-2"
              data-testid={ADMIN.loginBtn} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          )}
        </div>
      </div>

      {forgotOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(11,45,89,0.45)" }}>
          <div className="p2p-card p-6 max-w-sm w-full">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound className="w-5 h-5" style={{ color: "#0B2D59" }} />
              <h2 className="font-serif text-xl" style={{ color: "#0B2D59" }}>Reset password</h2>
            </div>
            <p className="text-sm" style={{ color: "#475569" }}>
              Enter the administrator email. A single-use, expiring reset link will be emailed to that address.
            </p>
            <form onSubmit={submitForgot} className="space-y-3 mt-4">
              <input type="email" className="p2p-input" placeholder="you@example.com"
                data-testid="admin-forgot-email"
                value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required autoComplete="email" />
              <div className="flex gap-2">
                <button type="button" onClick={() => { setForgotOpen(false); setForgotEmail(""); }}
                  className="p2p-btn-secondary flex-1" style={{ padding: "0.7rem 1rem" }}>Cancel</button>
                <button type="submit" className="p2p-btn-primary flex-1 inline-flex items-center justify-center gap-2"
                  data-testid="admin-forgot-submit"
                  style={{ padding: "0.7rem 1rem" }} disabled={forgotBusy}>
                  {forgotBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                  {forgotBusy ? "Sending…" : "Send link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
